if (!customElements.get("collection-load-more")) {
  class CollectionLoadMore extends HTMLElement {
    constructor() {
      super();

      this.handleClick = this.handleClick.bind(this);
      this.handleResize = this.handleResize.bind(this);

      this.setProperties();
      this.toggleProductsVisibility();

      window.addEventListener("shopify:section:load", () => {
        this.setProperties();
        this.toggleProductsVisibility();
      });

      if (this.btnLoadMore) {
        this.btnLoadMore.addEventListener("click", this.handleClick);
      }

      if (this.cardText) {
        window.addEventListener("resize", this.handleResize);
      }
    }

    setProperties() {
      this.productGrid = document.querySelector("#product-grid");
      this.cardText = this.productGrid?.querySelector(".js-card-text") || null;
      this.btnLoadMore = this.querySelector(".js-btn-load-more");
      this.skeletons = this.querySelector("[data-collection-skeletons]") || null;
      this.hiddenProducts = [];
      this.cardTextIndexOffset = 2;
      this.isLastPage = this.classList.contains("hidden");
      this.cardProduct = document.querySelector("card-product");
      this.setGridColsNumber();
    }

    setGridColsNumber() {
      if (!this.productGrid || !this.cardProduct) return;
      const gridWidth = this.productGrid.getBoundingClientRect().width;
      const cardWidth = this.cardProduct.getBoundingClientRect().width;
      this.productGridCols = Math.round(gridWidth / cardWidth);
    }

    handleClick(event) {
      event.preventDefault();
      if (!this.btnLoadMore) return;
      this.btnLoadMore.setAttribute("disabled", "true");
      this.showSkeletons();
      this.loadMoreProducts();
    }

    handleResize() {
      const { ShopTheme } = window;
      const util = ShopTheme?.utils;
      const breakpointMobile =
        typeof util?.isBelowBreakpoint === "function"
          ? util.isBelowBreakpoint("mobile", 750)
          : window.innerWidth < (ShopTheme?.breakpoints?.mobile || 750);
      if (breakpointMobile) {
        this.resetHiddenProducts();
        return;
      }
      this.toggleProductsVisibility();
    }

    toggleProductsVisibility() {
      if (!this.cardText || this.isLastPage) return;
      const gridChildren = Array.from(this.productGrid.children);
      const visibleItems = gridChildren
        .filter((item) => !item.classList.contains("hidden"))
        .reverse();
      const lastCard = visibleItems.find(
        (item) => !item.classList.contains("js-card-text")
      );
      if (!lastCard) return;

      const cardRect = lastCard.getBoundingClientRect();
      const firstVisible = visibleItems[0];
      const gridRect = this.productGrid.getBoundingClientRect();
      const firstRect = firstVisible.getBoundingClientRect();
      const cardsPerRow = Math.round(gridRect.width / cardRect.width);
      const gridEnd = gridRect.x + gridRect.width;
      const firstEnd = firstRect.x + firstRect.width;

      if (gridChildren[gridChildren.length - 1].classList.contains("js-card-text")) {
        const secondRect = visibleItems[1]?.getBoundingClientRect();
        const secondEnd = secondRect ? secondRect.x + secondRect.width : null;
        if (secondRect && firstRect.y !== secondRect.y && gridEnd !== secondEnd) {
          const firstHidden = this.hiddenProducts.shift();
          firstHidden?.classList.remove("hidden");
        }
      }

      if (gridEnd === firstEnd) {
        if (this.hiddenProducts.length >= cardsPerRow) {
          this.resetHiddenProducts(cardsPerRow);
        }
        return;
      }

      const remainingColumns = Math.round(
        (gridRect.width - (firstRect.x - gridRect.x)) / cardRect.width
      );

      if (this.hiddenProducts.length >= remainingColumns) {
        const surplus = this.hiddenProducts.length - remainingColumns;
        this.resetHiddenProducts(surplus);
        return;
      }

      let counter = 0;
      for (const item of visibleItems) {
        if (item.classList.contains("js-card-text")) continue;
        counter += 1;
        this.hiddenProducts.unshift(item);
        item.classList.add("hidden");
        if (counter === cardsPerRow - remainingColumns) break;
      }
    }

    resetHiddenProducts(limit) {
      if (!this.cardText) return;
      const items = [...this.hiddenProducts];
      let counter = 0;
      for (const item of items) {
        item.classList.remove("hidden");
        this.hiddenProducts.shift();
        counter += 1;
        if (limit && counter === limit) break;
      }
    }

    showSkeletons() {
      if (this.skeletons) {
        this.skeletons.hidden = false;
      }
    }

    hideSkeletons() {
      if (this.skeletons) {
        this.skeletons.hidden = true;
      }
    }

    async loadMoreProducts() {
      if (!this.btnLoadMore) return;
      try {
        const href = this.btnLoadMore.dataset.href;
        if (!href) {
          this.hideSkeletons();
          return;
        }

        const response = await fetch(`${href}`);
        const html = await response.text();
        const parser = new DOMParser().parseFromString(html, "text/html");
        const container = parser.querySelector("#ProductGridContainer");
        const fetchedGrid = container?.querySelector("#product-grid");
        const newLoadMore = container?.querySelector("collection-load-more");

        if (!container || !fetchedGrid || !newLoadMore) {
          this.hideSkeletons();
          return;
        }

        const newButton = newLoadMore.querySelector(".js-btn-load-more");
        const reachedEnd = newLoadMore.classList.contains("hidden");
        const children = fetchedGrid.children;

        if (this.cardText) {
          if (this.cardTextIndexOffset > 0) {
            const items = Array.from(children);
            const textIndex = items.findIndex((item) =>
              item.classList.contains("js-card-text")
            );
            if (textIndex !== -1) {
              const offsetIndex = textIndex - this.cardTextIndexOffset;
              const offsetTarget = children[offsetIndex];
              const cardTextElement = items[textIndex];
              this.cardTextIndexOffset = 0;
              if (offsetTarget && cardTextElement) {
                cardTextElement.remove();
                offsetTarget.insertAdjacentElement("beforebegin", cardTextElement);
              }
            }
          } else {
            this.cardTextIndexOffset = 2;
          }
        }

        this.resetHiddenProducts();

        if (
          href &&
          window.history &&
          typeof window.history.replaceState === "function"
        ) {
          history.replaceState({}, "", href);
        }

        this.productGrid?.insertAdjacentHTML("beforeend", fetchedGrid.innerHTML);
        this.hideSkeletons();

        if (reachedEnd) {
          this.isLastPage = true;
          this.classList.add("hidden");
          return;
        }

        this.toggleProductsVisibility();

        if (newButton) {
          this.btnLoadMore.dataset.href = newButton.dataset.href;
        }
      } catch (error) {
        console.error(error);
        alert(error.message);
        this.hideSkeletons();
      } finally {
        this.btnLoadMore?.removeAttribute("disabled");
        this.hideSkeletons();
      }
    }
  }

  customElements.define("collection-load-more", CollectionLoadMore);
}
